'use client'

import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { Store } from '@reduxjs/toolkit'
import { createWizardStore, useWizardSelector } from '@/components/ui/wizard/WizardState'
import { Provider, useDispatch } from 'react-redux'
import {
  SEARCH_PARAMETERS_UPDATED_EVENT_TYPE,
  TRAVEL_PERIOD_INPUT_PROPS,
  WizardStep,
} from '@/components/ui/wizard/Wizard'
import {
  getTimeSearchQuery,
  prefillTimeSearchFromQueryData,
  removeUndefinedEntries,
} from '@/components/ui/wizard/WizardImplementation'
import WizardInput from '@/components/ui/wizard/WizardInput'
import TravelPeriod from '@/components/ui/wizard/TravelPeriod'
import classNames from 'classnames'
import { ConfigContext } from '@/components/common/ConfigContext'
import { resetTravelPeriodSearch, setActiveStep } from '@/components/ui/wizard/WizardActions'
import moment from 'moment'
import { wizardLabels } from '@/lib/sharedConstants'
import { SearchPageContext } from '@/components/pages/search-page/SearchPageContext'
import { onMobile } from '@/lib/utils/utils'
import { useTranslations } from 'next-intl'

export default function HeaderTravelPeriodSelect(): ReactElement {
  const translate = useTranslations('SearchPage')
  const [store, setStore] = useState<Store>()
  const { setDisplayMap } = useContext(SearchPageContext)

  useEffect(() => {
    setStore(createWizardStore({ topics: [], destinations: [] }, translate))
  }, [])

  useEffect(() => {
    function hideMapOnMobile(): void {
      if (onMobile()) {
        setDisplayMap(false)
      }
    }
    document.addEventListener('resize', hideMapOnMobile)

    return () => document.removeEventListener('resize', hideMapOnMobile)
  }, [])

  return store ? (
    <Provider store={store}>
      <TravelPeriodSelectComponent />
    </Provider>
  ) : (
    <></>
  )
}

function TravelPeriodSelectComponent(): ReactElement {
  const dispatch = useDispatch()
  const travelPeriodSearchTerm = useWizardSelector<string>((s) => s.travelPeriodSearchTerm)
  const prefilled = useWizardSelector<boolean>((s) => s.prefilled)
  const [inputChangedCounter, setInputChangedCounter] = useState(0)
  const activeStep = useWizardSelector<number | undefined>((s) => s.activeStep)
  const domain = useContext(ConfigContext)!.domain
  const locale = useContext(ConfigContext)!.locale

  useEffect(() => {
    setInputChangedCounter(inputChangedCounter + 1)
    if (travelPeriodSearchTerm === '') {
      triggerSearch()
      setInputChangedCounter(0)
    }
  }, [travelPeriodSearchTerm])

  useEffect(() => {
    triggerSearch
  }, [prefilled])

  useEffect(() => {
    prefillFromQueryData()
  }, [])

  function prefillFromQueryData(): void {
    const searchParams = new URLSearchParams(window.location.search)

    prefillTimeSearchFromQueryData(searchParams, domain)
    moment.locale(locale)
  }

  function getTimeSearchQueryObject(): Record<string, string | number> {
    const state = window.store?.getState()

    if (!state) {
      return {}
    }

    return getTimeSearchQuery(
      state.selectedOptionDuration,
      state.flexibleTravelPeriodSelected,
      state.selectedOptionCalendarFlexibility,
      state.selectedOptionPeriods,
      state.startDate,
      state.endDate,
    )
  }

  function getSearchParameters(): Record<string, string | number> {
    return removeUndefinedEntries(getTimeSearchQueryObject())
  }

  function triggerSearch(): void {
    window.dispatchEvent(
      new CustomEvent(SEARCH_PARAMETERS_UPDATED_EVENT_TYPE, {
        detail: getSearchParameters(),
      }),
    )
  }

  return (
    <div
      className={classNames('mb-2 mt-4 md:ml-4', {
        'wm:fixed wm:left-0 wm:right-0 wm:top-0 wm:h-[100vh] wm:bg-white':
          activeStep === WizardStep.TRAVEL_PERIOD,
        'wm:max-w-[343px]': activeStep !== WizardStep.TRAVEL_PERIOD,
      })}
    >
      <WizardInput
        {...wizardLabels.travelPeriodInputProps}
        searchPage={false}
        highlighted={!travelPeriodSearchTerm}
        {...TRAVEL_PERIOD_INPUT_PROPS}
        searchTerm={travelPeriodSearchTerm}
        selectedValues={travelPeriodSearchTerm}
        step={WizardStep.TRAVEL_PERIOD}
        searchWizard={true}
        hideRightInputBorder={true}
        alwaysVisible={true}
        bookmarksPage={true}
        detailsBox={
          <TravelPeriod
            step={WizardStep.TRAVEL_PERIOD}
            search={() => false}
            searchWizard={true}
            nextButtonActiveLandingPage={false}
            primaryActionLabel="closeButton"
            absolutePositioned={true}
            hideSkipButton={true}
            additionalClassName=""
            specialNextAction={(e) => {
              dispatch(setActiveStep(-1))
              if (inputChangedCounter > 0) {
                triggerSearch()
              }
              setInputChangedCounter(0)
              e.stopPropagation()
            }}
          />
        }
        resetInput={resetTravelPeriodSearch}
        disableInputField={true}
        hideInputOnMobile={true}
        withBorders={true}
      />
    </div>
  )
}
